'use client';

import * as React from 'react';
import { ThemeProvider, useTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import {
  type ColorMode,
  type ColorTheme,
  DEFAULT_COLOR_MODE,
} from '@/lib/colors';
import { buildTheme } from '@/lib/theme';

const STORAGE_KEY = 'nserc-create.color-mode';

interface ColorModeContextValue {
  mode: ColorMode;
  setMode:    (mode: ColorMode) => void;
  toggleMode: () => void;
}

const ColorModeContext = React.createContext<ColorModeContextValue | null>(null);

export function ColorModeProvider({ children }: { children: React.ReactNode }) {
  // SSR + first paint always render in DEFAULT_COLOR_MODE (light). After
  // hydration, if a saved preference exists, we switch to it. A dark-mode
  // user will see one brief light frame on first load — acceptable tradeoff
  // for keeping SSR deterministic and avoiding hydration mismatches.
  const [mode, setModeState] = React.useState<ColorMode>(DEFAULT_COLOR_MODE);

  React.useEffect(() => {
    try {
      const saved = window.localStorage.getItem(STORAGE_KEY);
      if (saved === 'light' || saved === 'dark') {
        setModeState(saved);
      }
    } catch {
      // localStorage unavailable (private mode, SSR) — stick with default.
    }
  }, []);

  const setMode = React.useCallback((next: ColorMode) => {
    setModeState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Ignore quota/availability errors — mode still updates in-memory.
    }
  }, []);

  const toggleMode = React.useCallback(() => {
    setMode(mode === 'light' ? 'dark' : 'light');
  }, [mode, setMode]);

  const theme = React.useMemo(() => buildTheme(mode), [mode]);

  const ctx = React.useMemo<ColorModeContextValue>(
    () => ({ mode, setMode, toggleMode }),
    [mode, setMode, toggleMode]
  );

  return (
    <ColorModeContext.Provider value={ctx}>
      <ThemeProvider theme={theme}>
        <CssBaseline enableColorScheme />
        {children}
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}

// Read/set the current color mode. Use this in components that need to flip
// the theme (e.g. the toggle button in the header).
export function useColorMode(): ColorModeContextValue {
  const ctx = React.useContext(ColorModeContext);
  if (!ctx) {
    throw new Error('useColorMode must be used inside ColorModeProvider');
  }
  return ctx;
}

// Read the active color palette inside a functional component.
// For styled() callbacks, prefer ({ theme }) => theme.colors instead —
// styled callbacks don't re-run on context changes, only on theme changes,
// and the MUI theme is rebuilt whenever the mode flips.
export function useColorTheme(): ColorTheme {
  return useTheme().colors;
}
